import { ElementRef, Injectable, Injector } from "@angular/core";
import { AdStudioContainerSidebarState, AdStudioContainerStore, createInitialState } from "./ad-container.store";
import {
	CreativeUnitDto,
	CreativeUnitWithCustomizationsDto
} from "../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto";
import {
	LayerDto,
	LayerType,
	LayerWithDomContextDto
} from "../../../../../../api/src/creative-unit-package/models/layer/layer.dto";
import { AdStudioContainerQuery } from "./ad-container.query";
import { AdStudioContainerTabState } from "./ad-studio-container/ad-studio-container.model";
import { CreativeUnitPackageQuery } from "./ad-unit-package.query";
import { CreativeUnitPackageStore } from "./ad-unit-package.store";
import { CreativeUnitPackageService } from "./ad-unit-package.service";
import { CartStore } from "../cart/cart.store";
import { BehaviorSubject, map, pipe, take } from 'rxjs';
import {
	CustomizationOptionUtils
} from "../../../../../../api/src/creative-unit-package/utils/customization-option.utils";
import { EMPTY_OPTION, LOADING_OPTION } from './ad-container.data';
import { resolveDotNotationPath } from '../../utils/object.utils';
import { MergeTags } from '../../../../../../api/src/_core/utils/utils.merge-tags';
import {
	CustomizationItemDto,
	CustomizationSelectDto,
	CustomizationType,
	getCustomizationClassByType
} from "../../../../../../api/src/creative-unit-package/models/customization/customization.dto";
import {
	CreativeUnitPackageDto
} from "../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto";
import {
	CustomizationOptionDto
} from "../../../../../../api/src/creative-unit-package/models/customization/customization-option.dto";
import { QueryParams } from "../collection/collection.model";
import { PublicBusiness } from "../../../../../../api/src/business/business.entity";
import { CustomizationUtils } from "../../../../../../api/src/creative-unit-package/utils/customization.utils";
import { MergePropertiesUtils } from '../../../../../../api/src/creative-unit-package/utils/merge-properties.utils';
import { DataSourceService } from "../data-source/data-source.service";
import { CartItemGroup } from '../cart/cart.model';
import { LayersUtils } from "../../../../../../api/src/creative-unit-package/utils/layers.utils";
import { CreativeUnitUtils } from "../../../../../../api/src/creative-unit-package/utils/creative-unit.utils";
import {
	createLayoutGroupDefaults,
	LayoutGroupModel,
	LayoutMode
} from '../../../../../../api/src/creative-unit-package/models/layer/layout-group.dto';
import { LayoutStyleMutation, LayoutStyleUtils } from './layout';
import { AdAnimationService } from './ad-animations/ad-animations.service';

@Injectable({ providedIn: 'root' })
export class AdStudioContainerService {
	/** Package changes triggered by incremental Wizard selections */
	public pendingWizardChanges$: BehaviorSubject<{ [key: string]: any }> = new BehaviorSubject(undefined);

	constructor(
		public adStudioContainerQuery: AdStudioContainerQuery,
		public adStudioContainerStore: AdStudioContainerStore,
	public creativeUnitPackageQuery: CreativeUnitPackageQuery,
	public creativeUnitPackageStore: CreativeUnitPackageStore,
	public cartStore: CartStore,
	public dataSourceService: DataSourceService,
	private injector: Injector,
) {}

	private _creativeUnitPackageService: CreativeUnitPackageService | null = null;
	private _animationService: AdAnimationService | null = null;

	private get creativeUnitPackageService(): CreativeUnitPackageService {
		if (!this._creativeUnitPackageService) {
			this._creativeUnitPackageService = this.injector.get(CreativeUnitPackageService);
		}
		return this._creativeUnitPackageService;
	}

	private get animationService(): AdAnimationService {
		if (!this._animationService) {
			this._animationService = this.injector.get(AdAnimationService);
		}
		return this._animationService;
	}

	reset() {
		this.adStudioContainerStore.update(state => createInitialState());
	}

	setAppContext(context: 'editor' | 'preview' | 'review') {
		this.adStudioContainerStore.update(state => ({
			appContext: context
		}));
	}

	/**
	 * Enter review mode for comment selection.
	 * All visible layers become selectable, but editing is disabled.
	 */
	enterReviewMode(): void {
		// Auto-close inline editor if active
		if (this.adStudioContainerStore.getValue().interactions.inlineEditedLayer) {
			this.clearInlineEditedLayer();
		}
		this.adStudioContainerStore.update({ appContext: 'review' });
	}

	/**
	 * Exit review mode and return to preview mode.
	 */
	exitReviewMode(): void {
		this.adStudioContainerStore.update({ appContext: 'preview' });
	}

	/**
	 * Check if currently in review mode.
	 */
	isReviewMode(): boolean {
		return this.adStudioContainerStore.getValue().appContext === 'review';
	}

	setCustomizationsEditMode(editMode: boolean) {
		const current = this.adStudioContainerQuery.getValue().customizationsEditMode;
		if (current === editMode) {
			return;
		}

		this.adStudioContainerStore.update({ customizationsEditMode: editMode });
	}

	setPreviewContext(context: 'mockPreview' | 'preview') {
		this.adStudioContainerStore.update(state => ({
			previewContext: context
		}));
	}

	setAcceptedCreativePackageTermsDate(accepted: string) {
		this.adStudioContainerStore.update(_ => ({
			acceptedCreativePackageTermsDate: accepted
		}));
	}

	setCompletedWizard(completed: boolean) {
		this.adStudioContainerStore.update(_ => ({
			completedWizard: completed
		}));
	}

	setActivePreview(creativeUnitPreviewId: string) {
		if (this.adStudioContainerQuery.getValue().appContext === 'editor') {
			const previouslyActiveId = this.creativeUnitPackageQuery.getActiveCreativeUnitId();

			if (previouslyActiveId !== creativeUnitPreviewId) {
				console.log('[UNIT_SWITCH] Active unit changed from', previouslyActiveId, 'to', creativeUnitPreviewId);
			}

			this.creativeUnitPackageStore.update({ editor: { activeCreativeUnitId: creativeUnitPreviewId } });
		} else {
			//console.log('setting activeId', creativeUnitPreviewId);
			this.cartStore.update({ editor: { activeId: creativeUnitPreviewId } });
		}

		// Also set the active interaction unit ID if it is not already set.
		this.setInteractionUnitId(creativeUnitPreviewId);
	}

	setInteractionUnitId(interactUnitId: string) {
		// console.log('adContainerService: Setting interaction unit id', interactUnitId);
		this.adStudioContainerStore.update(state => ({
			...state,
			interactionUnitId: interactUnitId
		}));
	}

	updateInteractionsMode(newMode : Partial<{isMoving: boolean,	isResizing: boolean, isPanning: boolean}>) {
		this.adStudioContainerStore.update(state => ({
			...state,
			interactions: {
				...state.interactions,
				...newMode
			}
		}));
	}

	setSelectedGroupId(selectedGroupId: string) {
		// console.log('adContainerService: Setting selected group id', selectedGroupId);
		this.adStudioContainerStore.update(state => ({
			...state,
			selectedGroupId: selectedGroupId
		}));
	}

	/**
	 * Sets the seed used for cache busting asset URLs.
	 * @param seed
	 */
	setSeed(seed: number) {
		this.adStudioContainerStore.update({ seed });
	}

	/**
	 * Generates a new random seed between 0 and 999,999 and updates the store.
	 */
	generateSeed() {
		const seed = Math.floor(Math.random() * 1000000);
		this.adStudioContainerStore.update({ seed });
	}

	/**
	 * Sets the previewScaleValue with some guards.
	 */
	setPreviewScale(scale: number) {
		let scaleValue = scale?.toString();
		if (!scale) {
			scaleValue = 'fit';
		}

		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				previewScaleValue: scaleValue
			}
		}));
	}

	addToPreviewScale(scaleDifference: number) {
		const currentScale = parseFloat(this.adStudioContainerQuery.getValue()?.preview.previewScaleValue) || 1;

		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				previewScaleValue: (currentScale + scaleDifference).toString()
			}
		}));
	}

	/**
	 * Sets the fit adjusted preview scale.
	 */
	setFitAdjustedPreviewScale(scale: number) {
		let newScale = scale?.toString();
		if (!scale) {
			newScale = 'fit';
		}

		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				fitAdjustedPreviewScaleValue: newScale
			}
		}));
	}

	/**
	 * Sets the previewScaleValue with some guards.
	 */
	setPreviewPan(x: number, y: number ) {

		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				previewPan: { x, y }
			}
		}));
	}

	/**
	 * Sets the previewMaxHeightValue with some guards.
	 */
	setPreviewMaxHeight(maxHeight: number) {
		let maxHeightValue = maxHeight;
		if (!maxHeight) {
			maxHeightValue = createInitialState().preview.previewMaxHeightValue;
			return;
		}

		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				previewMaxHeightValue: maxHeightValue
			}
		}));
	}

	/**
	 * Sets the previewWidth property.
	 */
	setPreviewWidth(width: number, setStorage = true) {
		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				previewWidth: width
			}
		}));

		if (setStorage) {
			localStorage.setItem('AdStudioEditorPreviewWidth', width.toString());
		}
	}

	setSidebarTabState(tabState: AdStudioContainerTabState) {
		this.adStudioContainerStore.update(state => ({
			sidebar: {
				...state.sidebar,
				tabState: tabState
			}
		}));

		// Toggle review mode based on tab state
		if (tabState === AdStudioContainerTabState.REVIEW) {
			this.enterReviewMode();
		} else if (this.isReviewMode()) {
			this.exitReviewMode();
		}
	}

	resetSidebarState() {
		this.adStudioContainerStore.update(state => ({
			sidebar: {
				...state.sidebar,
				tabState: createInitialState().sidebar.tabState,
				sidebarState: createInitialState().sidebar.sidebarState
			}
		}));
	}

	setSidebarShowProperties(newState: {type?: 'layers' | 'customizations', entityId?: string} | null) {
		this.adStudioContainerStore.update(state => ({
			sidebar: {
				...state.sidebar,
				showProperties: newState
			}
		}));
	}

	setSidebarState(stateUpdate: Partial<AdStudioContainerSidebarState>, updatePartial = false) {
		this.adStudioContainerStore.update(state => ({
			sidebar: {
				...state.sidebar,
				...(updatePartial ? { ...stateUpdate } : stateUpdate)
			}
		}));
	}

	/**
	 * Sets the user's preferred animation editor mode
	 */
	setAnimationEditorMode(mode: 'preMade' | 'custom' | 'edit') {
		this.adStudioContainerStore.update(state => ({
			sidebar: {
				...state.sidebar,
				animationEditorMode: mode
			}
		}));
	}

	/**
	 * Gets the current animation editor mode
	 */
	getAnimationEditorMode(): 'preMade' | 'custom' | 'edit' {
		return this.adStudioContainerQuery.getValue().sidebar.animationEditorMode;
	}

	/**
	 * Filters a creative unit from the "All Items" list.
	 */
	filterCreativeUnits(units) {
		let filteredCreativeUnits = [];
		units.forEach(unit => {
			filteredCreativeUnits.push(unit.id);
		});

		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				filteredCreativeUnits: filteredCreativeUnits,
				visibleCreativeUnitPreviews: state.preview.visibleCreativeUnitPreviews.filter(unit => filteredCreativeUnits.includes(unit.id))
			}
		}));
	}

	/**
	 * Resets filtered creative units in the "All Items" list.
	 */
	resetFilteredCreativeUnits() {
		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				filteredCreativeUnits: [],
				visibleCreativeUnitPreviews: state.preview.creativeUnitPreviews
			}
		}));
	}

	/**
	 * Sets the active creative unit.
	 */
	setActiveGroupId(groupId: string) {
		this.adStudioContainerStore.update(state => ({
			activeGroupId: groupId
		}));
	}

	/**
	 * Hides a creative unit from the "All Items" list.
	 */
	hideCreativeUnit(unit) {
		this.adStudioContainerStore.update(state => {
			const newHidden = [...state.preview.hiddenCreativeUnits, unit.id];
			const newVisible = [...state.preview.visibleCreativeUnitPreviews].filter(u => u.id !== unit.id);

			return {
				preview: {
					...state.preview,
					hiddenCreativeUnits: newHidden,
					visibleCreativeUnitPreviews: newVisible
				}
			};
		});
	}

	/**
	 * Shows hidden creative units in the "All Items" list.
	 */
	showHiddenCreativeUnits() {
		this.adStudioContainerStore.update(state => {
			const newVisible = [...state.preview.creativeUnitPreviews].filter(unit => !state.preview.filteredCreativeUnits.includes(unit.id));

			return {
				preview: {
					...state.preview,
					hiddenCreativeUnits: [],
					visibleCreativeUnitPreviews: newVisible
				}
			};
		});
	}

	/**
	 * Update the customization expand state.
	 */
	updateCustomizationExpandState($event: { [p: string]: boolean }) {
		this.adStudioContainerStore.update(state => ({
			sidebar: {
				...state.sidebar,
				customizationExpandState: {
					...state.sidebar.customizationExpandState,
					...$event
				}
			}
		}));
	}

	upsertCreativeUnitPreviewElementRef(creativeUnitId: string, elementRef: ElementRef<any>) {
		this.adStudioContainerStore.update(state => {
			// Create a new object for creativeUnitPreviewElementRefs
			const creativeUnitPreviewElementRefs = {
				...state.preview.creativeUnitPreviewElementRefs,
				[creativeUnitId]: elementRef
			};

			return {
				preview: {
					...state.preview,
					creativeUnitPreviewElementRefs: creativeUnitPreviewElementRefs
				}
			};
		});
	}

	setCreativeUnitPreviews(creativeUnitPreviews: CreativeUnitWithCustomizationsDto[]) {
		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				creativeUnitPreviews: creativeUnitPreviews
			}
		}));
	}

	setVisibleCreativeUnitPreviews(creativeUnitPreviews: CreativeUnitWithCustomizationsDto[]) {
		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				visibleCreativeUnitPreviews: creativeUnitPreviews
			}
		}));
	}

	updateShowAllItemsState(showAllItemsState: boolean) {
		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				showAllItemsState: showAllItemsState
			}
		}));
	}

	updateMergeTagState(mergeState: { [key: string]: any }) {
		this.adStudioContainerStore.update(state => ({
			mergeTagState: mergeState
		}));
	}

	updateActiveCreativeUnitMergeState(mergeState: { [key: string]: any }) {
		this.adStudioContainerStore.update(state => ({
			activeCreativeUnitMergeState: mergeState
		}));
	}

	setActiveItemWidth(width: number) {
		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				activeItemWidth: width
			}
		}));
	}

	updateSyncAcrossPackageItems(syncAcrossPackageItems: boolean) {
		this.adStudioContainerStore.update(state => ({
			sidebar: {
				...state.sidebar,
				syncAcrossPackageItems: syncAcrossPackageItems
			}
		}));
	}

	setHoveredLayer(hoveredLayer: LayerWithDomContextDto) {
		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				hoveredLayer: hoveredLayer
			}
		}));
	}

	// ============================================
	// Comment Context Methods (Layer-linked & Timestamp-linked comments)
	// ============================================

	/**
	 * Sets the layer context for the comment being composed.
	 * Called when a layer is clicked while the comment input is focused.
	 */
	setCommentLayerContext(layer: LayerWithDomContextDto | null) {
		this.adStudioContainerStore.update(state => ({
			commentContext: {
				...state.commentContext,
				selectedLayerId: layer?.id ?? null
			}
		}));
	}

	/**
	 * Sets the timeline position for the comment being composed.
	 * Called when the comment input receives focus.
	 */
	setCommentTimelinePosition(position: number | null) {
		this.adStudioContainerStore.update(state => ({
			commentContext: {
				...state.commentContext,
				timelinePosition: position
			}
		}));
	}

	/**
	 * Clears all comment context (layer and timeline position).
	 * Called after a comment is successfully submitted.
	 */
	clearCommentContext() {
		this.adStudioContainerStore.update({
			commentContext: {
				selectedLayerId: null,
				timelinePosition: null
			}
		});
	}

	/**
	 * Jumps the timeline to the specified position.
	 * Used for timestamp-linked comments to navigate to the moment referenced.
	 * @param timestamp The position in seconds to seek to
	 */
	jumpToTimestamp(timestamp: number): void {
		// Coerce to number in case it comes as a string from template binding
		const numericTimestamp = Number(timestamp);
		if (numericTimestamp == null || !Number.isFinite(numericTimestamp) || numericTimestamp < 0) {
			return;
		}
		// Update the animation state's seek position via the animation service
		this.animationService.updateSeek(numericTimestamp);
	}

	setLassoActive(isActive: boolean) {
		//console.log('[LASSO] setLassoActive:', isActive);
		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				isLassoActive: isActive
			}
		}));
	}

	setSelectedLayers(selectedLayers: LayerWithDomContextDto[]) {
		const activeCreativeUnitId = this.creativeUnitPackageQuery.getActiveCreativeUnitId();
		const selectedLayersWithElements: LayerWithDomContextDto[] = (selectedLayers || []).map(layer => {
			// If the layer is a LayerDto, we need to find the element.
			if (!layer.element) {
				//console.log(activeCreativeUnitId, this.adStudioContainerQuery.getValue().preview.creativeUnitPreviewElementRefs)
				return this.convertLayerDtoToLayerWithDomContextDto(layer, activeCreativeUnitId);
			}
			return layer;
		});

		// Log every call to setSelectedLayers with stack trace and arguments


		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				selectedLayers: selectedLayersWithElements
			}
		}));

		// Set the interaction unit ID if we have selected layers and an active creative unit

		if (selectedLayersWithElements.length > 0 && activeCreativeUnitId) {
			this.setInteractionUnitId(activeCreativeUnitId);
		}

		if (selectedLayers?.[0]?.id){
			this.setSidebarShowProperties({type: 'layers', entityId: selectedLayers[0]?.id });
		}

		// Also update comment layer context for layer-linked comments
		// When a layer is selected, it becomes the context for any comment being composed
		this.setCommentLayerContext(selectedLayersWithElements?.[0] ?? null);
	}

	convertLayerDtoToLayerWithDomContextDto(layer: LayerDto, activeCreativeUnitId: string): LayerWithDomContextDto {
		if (!activeCreativeUnitId) {
			activeCreativeUnitId = this.creativeUnitPackageQuery.getActiveCreativeUnitId();
		}

		const element = this.adStudioContainerQuery.getValue()
			.preview.creativeUnitPreviewElementRefs[activeCreativeUnitId]?.nativeElement.querySelector(`.l-${layer.id}`);

		// Convert the LayerDto to a LayerWithDomContextDto and add the element.
		return {
			...layer,
			creativeUnitId: activeCreativeUnitId,
			element: element
		};
	}

	updateLayerLayoutGroup(layer: LayerWithDomContextDto, mutation: LayoutStyleMutation, activeUnitId?: string) {
		const layerId = layer.id;
		const pkg = this.creativeUnitPackageQuery.getActive();

		if (!pkg) {
			return;
		}

		const applyLayoutUpdate = (layer: LayerDto): LayerDto => {
			let updatedLayer = layer;

			if (layer.id === layerId) {
				const existingGroup = layer.group ?? {};

				const nextGroup = {
					...existingGroup,
					layout: mutation.layout
				} as LayerDto['group'] & { layout: LayoutGroupModel; flow?: 'freeform' | 'legacy' };

				if (mutation.layout.mode === LayoutMode.Freeform) {
					nextGroup.flow = 'freeform';
				} else if (!nextGroup.flow || nextGroup.flow === 'freeform') {
					nextGroup.flow = 'legacy';
				}

				updatedLayer = {
					...layer,
					group: nextGroup,
					style: mutation.stylePatch
						? this.mergeLayerStyle(layer.style, mutation.stylePatch)
						: layer.style
				};
			}

			const childPatch = mutation.childStylePatches?.[layer.id];
			if (childPatch) {
				updatedLayer = {
					...updatedLayer,
					style: this.mergeLayerStyle(updatedLayer.style, childPatch)
				};
			}

			return updatedLayer;
		};

		const updatedPackage: CreativeUnitPackageDto = {
			...pkg
		};

		// if package update -> update all pkg layers and unit layers
		if (!activeUnitId) {
			const updatedPackageLayers = LayersUtils.mapAllLayers(pkg.layers, applyLayoutUpdate);
			updatedPackage.layers = updatedPackageLayers;
			const updatedCreativeUnits = pkg.creativeUnits?.map(unit => {
				console.log(unit.id)
				return {
					...unit,
					layers: LayersUtils.mapAllLayers(unit.layers, applyLayoutUpdate)
				}
			});

			updatedPackage.creativeUnits = updatedCreativeUnits;


		// if sspecific unit update -> update only that unit layers
		} else {
			const updatedCreativeUnits = pkg.creativeUnits?.map(unit => {
				console.log(unit.id)
				return {
					...unit,
					layers: unit.id == activeUnitId ? LayersUtils.mapAllLayers(unit.layers, applyLayoutUpdate) : unit.layers
				}
			});

			updatedPackage.creativeUnits = updatedCreativeUnits;
		}

		this.creativeUnitPackageService.update(updatedPackage).pipe(take(1)).subscribe();
		this.creativeUnitPackageService.pushHistoryEvent({
			packageId: pkg.id,
			changes: pkg
		});
	}

	private mergeLayerStyle(
		existing: LayerDto['style'] | undefined,
		patch: Partial<LayerDto['style']>
	): LayerDto['style'] | undefined {
		const nextStyle = {
			...(existing ?? {})
		};

		for (const [key, value] of Object.entries(patch ?? {})) {
			if (value === undefined) {
				delete nextStyle[key];
			} else {
				nextStyle[key] = value;
			}
		}

		return Object.keys(nextStyle).length ? nextStyle : undefined;
	}


	addSelectedLayer(selectedLayer: LayerWithDomContextDto) {
		if (!selectedLayer) {
			return;
		}

		// Add the selected element to the selected elements.
		let selectedLayers = this.adStudioContainerStore.getValue().interactions.selectedLayers;
		if (selectedLayers.find(element => element.id === selectedLayer.id)) {
			return;
		}

		// Filter out any selected elements from other creative units.
		selectedLayers = selectedLayers.filter(element => element.creativeUnitId === selectedLayer.creativeUnitId);

		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				selectedLayers: [...state.interactions.selectedLayers, selectedLayer].filter(element => element !== undefined)
			}
		}));
	}

	removeSelectedLayer(selectedLayerId: LayerWithDomContextDto['id']) {
		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				selectedLayers: state.interactions.selectedLayers.filter(element => element.id !== selectedLayerId)
			}
		}));
	}

	clearSelectedLayers() {
		// console.log('Clearing selected layers');
		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				selectedLayers: []
			}
		}));
		this.setImageCropMode(false);

		// Clear comment layer context when layers are deselected
		this.setCommentLayerContext(null);
	}


	setInlineEditedLayer(editedLayer: LayerWithDomContextDto) {
		if (!editedLayer?.element) {
			const activeCreativeUnitId = this.creativeUnitPackageQuery.getActiveCreativeUnitId();
			editedLayer = this.convertLayerDtoToLayerWithDomContextDto(editedLayer, activeCreativeUnitId);
		}


		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				inlineEditedLayer: editedLayer
			}
		}));
	}

	clearInlineEditedLayer() {
		this.adStudioContainerStore.update(state => ({
			interactions: {
				...state.interactions,
				inlineEditedLayer: undefined
			}
		}));
	}

	setPendingWizardChanges(wizardChanges: { [key: string]: any }) {
		this.pendingWizardChanges$.next(wizardChanges);
	}

	setCustomizationOptions(id: string, options: CustomizationOptionDto[], params: QueryParams) {
		this.adStudioContainerStore.update(state => ({
			customizations: {
				...state.customizations,
				options: {
					...state.customizations.options,
					[id]: {
						options: options,
						queryParams: params
					}
				}
			}
		}));
	}

	setCustomizationOptionCache(hash: string, options: CustomizationOptionDto[], loading: boolean) {
		this.adStudioContainerStore.update(state => ({
			customizations: {
				...state.customizations,
				optionCache: {
					...state.customizations.optionCache,
					[hash]: {
						options: options,
						loading: loading
					}
				}
			}
		}));
	}

	setCustomizationPendingFetchMap(hash: string, promise: Promise<CustomizationOptionDto[]>) {
		this.adStudioContainerStore.update(state => ({
			customizations: {
				...state.customizations,
				pendingFetchMap: {
					...state.customizations.pendingFetchMap,
					[hash]: promise
				}
			}
		}));
	}

	removeCustomizationPendingFetchMapHash(hash: string) {
		this.adStudioContainerStore.update(state => {
			const { [hash]: _, ...rest } = state.customizations?.pendingFetchMap || {};
			return {
				customizations: {
					...state.customizations,
					customizationPendingFetchMap: rest
				}
			};
		});
	}

	/**
	 * Applies dynamic default/placeholder values to a tree of customizations in-order.
	 *
	 * Behavior:
	 * - Flattens once, then iterates; if `onlyApplyDownstreamOfId` is set, processing starts with that id.
	 * - Lazily computes `mergeState` and invalidates only when a customization's value changes.
	 * - Writes only when something changes (value/dirty/placeholder/parentId), removes `parentId`.
	 * - Updates a single customization via `updateCustomizationById`
	 *
	 * Assumptions:
	 *  `mergeState` depends on values only; if it depends on other fields, bump the cache version accordingly.
	 */
	async applyDynamicDefaultValues(
		customizations: CustomizationItemDto[],
		pkg: CreativeUnitPackageDto,
		business?: PublicBusiness,
		unit?: CreativeUnitDto,
		onlyApplyDownstreamOfId?: string, // in case of triggered by single customization change
		activeGroup?: CartItemGroup
	): Promise<CustomizationItemDto[]> {
		let workingCustomizations = customizations;

		let workingUnit = unit ? { ...unit, customizations: workingCustomizations } : undefined;
		let workingPkg = { ...pkg, ...(unit ? {} : { customizations: workingCustomizations }) };

		const flattenedCustomizations = CustomizationUtils.findAllCustomizations(customizations);

		//customizationMap
		const buildIdMap = () =>
			new Map(CustomizationUtils.findAllCustomizations(workingCustomizations).map((c: CustomizationItemDto) => [c.id, c] as const));
		let idToCurrent = buildIdMap();

		let stateVersion = 0;
		let cachedVersion = -1;
		let cachedMergeState: any | null = null;

		const ensureMergeState = () => {
			if (cachedVersion !== stateVersion) {
				cachedMergeState = MergePropertiesUtils.getMergeableProperties(workingPkg, business, workingUnit, activeGroup);
				cachedVersion = stateVersion;
			}
			return cachedMergeState;
		};

		let startIndex = 0;
		if (onlyApplyDownstreamOfId) {
			const index = flattenedCustomizations.findIndex(c => c.id === onlyApplyDownstreamOfId);
			startIndex = index >= 0 ? index : 0;
		}

		for (let i = startIndex; i < flattenedCustomizations.length; i++) {
			const id = flattenedCustomizations[i].id;
			const current = idToCurrent.get(id);
			if (!current) continue;

			const mergeState = ensureMergeState();

			let layers = workingUnit?.layers || workingPkg?.layers || [];
			if (
				current.type === CustomizationType.VIDEO_PICKER ||
				current.type === CustomizationType.IMAGE_PICKER ||
				current.type === CustomizationType.TEXT ||
				current.type === CustomizationType.AUDIO_PICKER
			) {
				const cLayers = CreativeUnitUtils.getLayersFromCustomization(current, layers) || [];
				const visibleLayers = LayersUtils.findAllVisibleLayers(cLayers, mergeState);
				if (visibleLayers?.length) layers = visibleLayers;
			}

			let dynamicOptions: CustomizationOptionDto[] | undefined;
			if (current.type === CustomizationType.SELECT && current.dynamicLink) {
				dynamicOptions = await this.getDynamicOptions(current, mergeState);
			}

			const customizationClass = getCustomizationClassByType(current.type);
			const defaultValueResult = customizationClass?.getDefaultValue(current as any, layers, mergeState, dynamicOptions);
			const { value, dirty } = CustomizationUtils.unwrapDefaultCustomizationValue(defaultValueResult, current.dirty);
			const placeholder = customizationClass.getPlaceholderValue(current as any, layers);

			const hasValueChanged =
				(value === null || value === undefined) && (current.value == null || current.value === undefined)
					? false
					: JSON.stringify(value) !== JSON.stringify(current.value);

			const willChange =
				hasValueChanged ||
				dirty !== current.dirty ||
				(current as any).placeholder !== placeholder ||
				'parentId' in (current as any);

			if (!willChange) continue;

			// if value changed, bump the state version so that merge tags are recalculated
			if (hasValueChanged) {
				stateVersion++;
			}

			// Build the replacement
			const newCustomization: CustomizationItemDto & { placeholder?: any } = {
				...(current as any),
				dirty,
				placeholder,
				value
			};
			delete (newCustomization as any).parentId;

			workingCustomizations = CustomizationUtils.updateCustomizationById(workingCustomizations, id, () => newCustomization);

			if (unit) {
				workingUnit = { ...workingUnit, customizations: workingCustomizations };
			} else {
				workingPkg = { ...workingPkg, customizations: workingCustomizations };
			}

			// update the map with the new customization
			idToCurrent.set(id, newCustomization);
		}

		return workingCustomizations;
	}

	public async getDynamicOptions(customization: CustomizationSelectDto, mergeTagState: { [key: string]: any }): Promise<CustomizationOptionDto[]> {
		const params: QueryParams = MergePropertiesUtils.getMergedParams(customization.dynamicLink.params || {}, mergeTagState);
		const newHash: string = JSON.stringify({ dataSourceSlug: customization.dynamicLink?.dataSourceSlug, labelKey: customization?.dynamicLink.labelKey, ...params  });

		if (!customization.dynamicLink?.dataSourceSlug) {
			console.warn('Dynamic link is missing a dataSourceSlug', customization);
			this.setCustomizationOptions(customization.id, [EMPTY_OPTION], params)
			return [EMPTY_OPTION];
		}

		// check if we've already cached a call to this query with the same parameters
		const cachedValue = this.adStudioContainerQuery.getCustomizationOptionCacheByHash(newHash);

		// do we already have it cached?
		if (cachedValue?.loading === false && cachedValue?.options?.length && !cachedValue?.options?.includes(LOADING_OPTION)) {
			let availableOptions = CustomizationOptionUtils.processOptions(customization, cachedValue.options, mergeTagState)

			if (!availableOptions?.length) {
				availableOptions = [EMPTY_OPTION];
			}

			this.setCustomizationOptions(customization.id, availableOptions, params);
			return availableOptions;
		} else { // if not already cached, we need to fetch it or wait for it to finish fetching
			this.setCustomizationOptions(customization.id, [LOADING_OPTION], params);

			// if not already fetching, then start fetching it
			if (cachedValue?.loading !== true) {
				this.setCustomizationOptionCache(newHash, [LOADING_OPTION], true);

				const pendingFetch = this.fetchDynamicOptions(customization, params, newHash, mergeTagState);
				this.setCustomizationPendingFetchMap(newHash, pendingFetch);
			}

			// wait for the promise to resolve (i.e. for us to get the options)
			const options = await this.adStudioContainerQuery.getCustomizationPendingFetchByHash(newHash);

			if (options) {
				let availableOptions = CustomizationOptionUtils.processOptions(customization, options, mergeTagState);

				if (!availableOptions?.length) {
					availableOptions = [EMPTY_OPTION];
				}

				this.setCustomizationOptions(customization.id, availableOptions, params);
				return availableOptions;
			}
		}
		return [EMPTY_OPTION];
	}

	public toggleTemplateIncludesInteractions(state?: boolean) {
		// console.log('Toggling templateIncludesInteractions', state);
		if (typeof state === 'undefined') {
			state = !this.adStudioContainerQuery.getValue().interactions.templateIncludesInteractions;
		}
		this.adStudioContainerStore.update(s => ({
			interactions: {
				...s.interactions,
				templateIncludesInteractions: state
			}
		}));
	}

	public toggleEnableInteractions(state?: boolean) {
		// console.log('Toggling enable interactions', state);
		if (typeof state === 'undefined') {
			state = !this.adStudioContainerQuery.getValue().interactions.enableInteractions;
		}
		this.adStudioContainerStore.update(s => ({
			interactions: {
				...s.interactions,
				enableInteractions: state
			}
		}));
	}

	public toggleShowAnimationTimeline(state?: boolean) {
		if (typeof state === 'undefined') {
			state = !this.adStudioContainerQuery.getValue().showAnimationTimeline;
		}
		this.adStudioContainerStore.update(s => ({
			showAnimationTimeline: state,
			animationTimelineHeight: state ? s.animationTimelineHeight : undefined
		}));
	}

	public setAnimationTimelineHeight(newHeight?: number) {
		this.adStudioContainerStore.update(s => ({
			animationTimelineHeight: newHeight
		}));
	}
	public setAnimationTimelineLayersWidth(newWidth?: number) {
		this.adStudioContainerStore.update(s => ({
			animationTimelineLayersWidth: newWidth
		}));
	}

	public setStoreSetting(key: string, value: any) {
		this.adStudioContainerStore.update(s => ({
			[key]: value
		}));

	}


	private fetchDynamicOptions(customization: CustomizationSelectDto, params: QueryParams, hash: string, mergeTagState: { [key: string]: any }) {

		return new Promise<CustomizationOptionDto[]>(resolve => {
			this.dataSourceService.query(customization.dynamicLink?.dataSourceSlug, params).pipe(
				map(response => response.data?.results),
				pipe(take(1))
			).subscribe(response => {
				let newOptions = [];

				if (response?.length) {
					newOptions = newOptions.concat(
						response
							.filter(result => result.id !== undefined)
							.map((result: any) => {
								let locales: any = {};
								try {
									locales = JSON.parse(
										MergeTags.applyMergeTagsToString(JSON.stringify(customization.dynamicLink.mapping?.locales), {...result, ...mergeTagState})
									);
								} catch (e) {
									console.warn('Unable to parse dynamic link mapping', e);
								}

								let resolvedLabel = resolveDotNotationPath(customization.dynamicLink.labelKey, result);

								if (locales) {
									const localesLabelKey = locales?.['labelKey']?.[localStorage.getItem('localeCode')];

									if (localesLabelKey) {
										resolvedLabel = resolveDotNotationPath(localesLabelKey, result) || resolvedLabel;
									}
								}

								// map as little as possible to avoid doing the same thing over and over later one for each customization using the same datasource
								return CustomizationOptionUtils.create({
									id: result.id,
									label: resolvedLabel,
									metadata: result
								});
							})
					);
				}

				if (!newOptions?.length) {
					newOptions.push(EMPTY_OPTION);
				}

				this.setCustomizationOptionCache(hash, newOptions, false);
				this.removeCustomizationPendingFetchMapHash(hash);

				resolve(newOptions);
			}, _ => {
				resolve([EMPTY_OPTION]);
			});
		})
	}


	/**
	 * Toggles selectUnitMode. If a state is provided, sets it; otherwise, inverts the current state.
	 */
	toggleSelectUnitMode(state?: boolean): void {
		const current = this.adStudioContainerQuery.getValue().selectUnitMode;
		this.adStudioContainerStore.update(s => ({
			selectUnitMode: state !== undefined ? state : !current
		}));
	}

	/**
	 * If we are not in the desired selectUnitMode state, then set it.
	 * @param state
	 */
	private confirmSelectUnitMode(state?: boolean): void {
		if (this.adStudioContainerQuery.getValue().selectUnitMode !== state) {
			this.toggleSelectUnitMode(state);
		}
	}

	/**
	 * Selects a creative unit by id (adds to selectedUnits if not already present).
	 */
	selectCreativeUnit(unitId: string): void {
		const selectedUnits = this.adStudioContainerQuery.getValue().selectedUnits || [];
		if (!selectedUnits.includes(unitId)) {
			this.adStudioContainerStore.update(s => ({
				selectedUnits: [...selectedUnits, unitId]
			}));
		}

		this.confirmSelectUnitMode(true);
	}

	/**
	 * Selects a creative unit by id (adds to selectedUnits if not already present).
	 */
	setSelectedCreativeUnits(unitIds: string[]): void {
		this.adStudioContainerStore.update(_ => ({
			selectedUnits: unitIds
		}));

		this.confirmSelectUnitMode(true);
	}

	/**
	 * Unselects a creative unit by id (removes from selectedUnits if present).
	 */
	unselectCreativeUnit(unitId: string): void {
		const selectedUnits = this.adStudioContainerQuery.getValue().selectedUnits || [];
		if (selectedUnits.includes(unitId)) {
			this.adStudioContainerStore.update(s => ({
				selectedUnits: selectedUnits.filter(id => id !== unitId)
			}));
		}

		// If no units are selected, exit select unit mode.
		if (this.adStudioContainerQuery.getValue().selectedUnits.length === 0) {
			this.toggleSelectUnitMode(false);
		}
	}

	clearSelectedUnits(): void {
		this.adStudioContainerStore.update(s => ({
				selectedUnits: [],
				selectUnitMode: false
		}));
	}

	/**
	 * Sets the crop box mode state.
	 */
	setCropBoxMode(cropBoxMode: boolean) {
		this.adStudioContainerStore.update(state => ({
			preview: {
				...state.preview,
				cropBoxMode: cropBoxMode
			}
		}));
	}

	/**
	 * Exits crop box mode.
	 */
	exitCropBoxMode() {
		this.setCropBoxMode(false);
	}

	setImageCropMode(active: boolean, layer: LayerDto = undefined) {
		this.adStudioContainerStore.update(_ => ({
			cropMode: {
				active: active,
				layerId: layer?.id,
				creativeUnitId: layer?.creativeUnitId,
				initialCropData:  layer?.cropData
			}
		}));
	}

	triggerFlowElements(layerToTrigger: string, packageId: string, creativeUnitId: string | null) {
		if (!packageId && !creativeUnitId) {
			return;
		}

		const pkg = this.creativeUnitPackageQuery.getEntity(packageId);

		if (!pkg) {
			return;
		}

		if (!creativeUnitId) {
			const groupToTriggerId = LayersUtils.flattenLayers(pkg.layers).find(layer => layer.id === layerToTrigger)?.id;
			if (!groupToTriggerId) {
				return;
			}

			pkg.creativeUnits.forEach(unit => {
				const unitLayer = LayersUtils.flattenLayers(unit.layers).find(l => l.id === groupToTriggerId);

				if (unitLayer) {
					const currentUnitLayout = unitLayer?.group?.layout;
					if (currentUnitLayout?.mode == LayoutMode.Freeform) {
						return;
					}

					// Only pass child IDs if the mode is changing (to trigger fit container resets) or intentionally triggered
					const unitChildLayerIds = unitLayer.layers?.map(child => child.id) ?? [];

					let unitFixedSizeChildren = [];

					if (unitChildLayerIds) {
						unitChildLayerIds.forEach(childId => {
							const foundUnitLayer = unitLayer.layers.find(child => child.id === childId);

							if (!this.isFitContainerWidth(foundUnitLayer, unit.id) || !this.isFitContainerHeight(foundUnitLayer, unit.id)) {
								unitFixedSizeChildren.push(childId);
							}
						})
					}
					const mutation = LayoutStyleUtils.applyLayoutChange(currentUnitLayout, {}, unitChildLayerIds, unitFixedSizeChildren, true);
					this.updateLayerLayoutGroup(unitLayer as any, mutation, unit.id);
				}
			})
		}

		// creative unit specific
		else {
			const unit = pkg.creativeUnits.find(u => u.id === creativeUnitId);

			const unitLayer = LayersUtils.flattenLayers(unit.layers).find(layer => layer.id === layerToTrigger);

			if (!unitLayer) {
				return;
			}

			if (unitLayer) {
				const currentUnitLayout = unitLayer?.group?.layout;
				if (currentUnitLayout?.mode == LayoutMode.Freeform) {
					return;
				}

				// Only pass child IDs if the mode is changing (to trigger fit container resets) or intentionally triggered
				const unitChildLayerIds = unitLayer.layers?.map(child => child.id) ?? [];

				let unitFixedSizeChildren = [];

				if (unitChildLayerIds) {
					unitChildLayerIds.forEach(childId => {
						const foundUnitLayer = unitLayer.layers.find(child => child.id === childId);

						if (!this.isFitContainerWidth(foundUnitLayer, unit.id) || !this.isFitContainerHeight(foundUnitLayer, unit.id)) {
							unitFixedSizeChildren.push(childId);
						}
					})
				}
				const mutation = LayoutStyleUtils.applyLayoutChange(currentUnitLayout, {}, unitChildLayerIds, unitFixedSizeChildren, true);
				this.updateLayerLayoutGroup(unitLayer as any, mutation, unit.id);
			}
		}
	}

	getLayout(layer: LayerWithDomContextDto | LayerDto): LayoutGroupModel {
		return this.adStudioContainerQuery.getLayoutGroup(layer.id) ?? createLayoutGroupDefaults();
	}

	/**
	 * Check if the width is set to fit container (flex: 1)
	 */
	isFitContainerWidth(layer: LayerDto, unitId: string): boolean {
		const parentLayout = this.getParentLayoutMode(layer, unitId);
		if (!parentLayout || parentLayout === LayoutMode.Freeform) return false;

		const hasFlex = layer.style?.flex === '1';
		const hasWidthPercentage = layer.style?.width === '100%';

		// Horizontal flex: fit container = flex: 1
		// Vertical flex: fit container = width: 100%
		// Grid: fit container = width: 100%
		if (parentLayout === LayoutMode.FlexHorizontal) {
			return hasFlex;
		} else {
			return hasWidthPercentage;
		}
	}

	/**
	 * Check if the height is set to fit container (flex: 1 or 100%)
	 */
	isFitContainerHeight(layer: LayerDto, unitId: string): boolean {
		const parentLayout = this.getParentLayoutMode(layer, unitId);
		if (!parentLayout || parentLayout === LayoutMode.Freeform) return false;

		const hasFlex = layer.style?.flex === '1';
		const hasHeightPercentage = layer.style?.height === '100%';

		// Vertical flex: fit container = flex: 1
		// Horizontal flex: fit container = height: 100%
		// Grid: fit container = height: 100%
		if (parentLayout === LayoutMode.FlexVertical) {
			return hasFlex;
		} else {
			return hasHeightPercentage;
		}
	}

	private getParentLayoutMode(layer: LayerDto, unitId: string): LayoutMode | null {
		const pkg = this.creativeUnitPackageQuery.getActive();
		if (!pkg) {
			return null;
		}

		const creativeUnitLayer = pkg.creativeUnits.find(l => l.id === unitId);
		const unitLayers = LayersUtils.flattenLayers(creativeUnitLayer?.layers)  || [];
		const unitParentGroup = unitLayers.find(l =>
			l.type === LayerType.GROUP &&
			l.layers?.some(child => child.id === layer.id)
		);

		// Find parent group layer
		const allLayers = LayersUtils.flattenLayers(pkg.layers);
		const parentGroup = allLayers.find(l =>
			l.type === LayerType.GROUP &&
			l.layers?.some(child => child.id === layer.id)
		);

		return unitParentGroup?.group?.layout?.mode ?? parentGroup?.group?.layout?.mode ?? null;
	}
}
